//	Internals
import { isEmpty } from "lodash";
//	Externals
import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { URL } from "../../../constants/URL";
import {
	useGetGiftsDetails,
	useGetRestaurantSettings,
} from "../../../queryHooks";
import { useGetTransactionsDetails } from "../../../queryHooks";
import CS from "../../../services/commonService";

import getBadgeClasses from "../../../util/badgeClass";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import BreadCrumbs from "../../common/breadcrumbs";
import { RefundModal, CompleteOrderModal } from "../../common/RefundModal";
import { authenticatedApi } from "../../../services/api";
import { MICROSERVICE_CONFIG } from "../../../config/configs";
// Component starts
const GiftOrderDetails = () => {
	//	Pre-defined values
	const restaurantInfo = {
		restaurantName: "",
		address: "",
		emailId: "",
		photo: "",
	};
	const giftedToUser = {
		phoneNumber: "",
		fullName: "",
		emailId: "",
		photo: "",
	};
	const giftedByUser = {
		phoneNumber: "",
		fullName: "",
		emailId: "",
		photo: "",
	};
	//	Params
	const { slug } = useParams();
	const location = useLocation();

	// Check if this is accessed from pickup orders
	const isFromPickup = location.state?.source === "pickup";

	const { data: dataGiftsDetails } = useGetGiftsDetails(slug);

	const { data: dataGiftsDetailsOrders } = useGetTransactionsDetails(slug);

	//  Navigation
	//const navigate = useNavigate();
	//	State values
	const [orderData] = useState({});
	const [restaurantID, setRestaurantID] = useState();
	const [isRefunding, setIsRefunding] = useState(false);
	const [refundStatus, setRefundStatus] = useState(null);
	const [showRefundModal, setShowRefundModal] = useState(false);
	const [isCompleting, setIsCompleting] = useState(false);
	const [completeStatus, setCompleteStatus] = useState(null);
	const [showCompleteModal, setShowCompleteModal] = useState(false);
	//	Manipulate the data
	const orderDetails = {
		isTipAdded: false,
		offerApplied: false,
		offerId: "",
		currencyGiftApplied: false,
		currencyGiftId: "",
		orderNumber: "",
		orderType: "",
		orderStatusHist: [],
		items: [],
		subTotal: "",
		tipAmount: "",
		discount: "",
		totalAmount: "",
		tax: "",
		commission: "",
		amountToPayRest: "",
		amountToAdmin: "",
		billSplitStatus: "",
		orderStatus: "",
		sharedBillFriends: false,
		friends: [],
		rewardPointsUsed: false,
		rewardPointsValue: "",
	};

	const {
		restaurantId,
		giftedTo,
		giftedBy,

		paymentDetail,
		orderId = { ...orderDetails },
	} = orderData;

	const {
		isTipAdded = false,
		offerApplied = false,
		offerId,
		currencyGiftApplied = false,
		currencyGiftId,
		walletAmount,

		orderType,

		subTotal,
		tipAmount,
		discount,
		totalAmount,
		amountCustomerPaid,
		tax,
		totalMerchantFee,
		totalCommission,
		amountToPayRest,
		billSplitStatus = false,
		orderStatus,
		sharedBillFriends = false,
		friends = [],
		rewardPointsUsed = false,
		rewardPointsValue,
	} = orderId;

	useEffect(() => {
		setRestaurantID(dataGiftsDetails?.data?.restaurant?.id);
	}, [dataGiftsDetails]);

	const { data: restaurantConfigData } = useGetRestaurantSettings(restaurantID);

	const handleRefundClick = () => {
		setShowRefundModal(true);
	};

	const handleRefundConfirm = async () => {
		setShowRefundModal(false);
		setIsRefunding(true);
		setRefundStatus(null);

		try {
			const response = await authenticatedApi.post(
				`${MICROSERVICE_CONFIG.ACCOUNTING_SERVICE}/admin/orders/${slug}/refund`
			);

			if (response.data.status === "success") {
				setRefundStatus({
					type: "success",
					message: `Refund processed successfully! Refund ID: ${response.data.data.refund_id}`,
				});
				// Refresh the page data or redirect
				window.location.reload();
			}
		} catch (error) {
			console.error("Refund error:", error);
			let errorMessage = "An error occurred while processing the refund.";

			if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.response?.data?.error) {
				errorMessage = error.response.data.error;
			}

			setRefundStatus({
				type: "error",
				message: errorMessage,
			});
		} finally {
			setIsRefunding(false);
		}
	};

	const canRefund = () => {
		const paymentStatus = dataGiftsDetails?.data?.payment_status?.value;
		const orderStatus = dataGiftsDetails?.data?.status?.value;

		return (
			paymentStatus === "SUCCESSFUL" &&
			orderStatus !== "EXPIRED" &&
			orderStatus !== "COMPLETED"
		);
	};

	const canComplete = () => {
		const orderStatus = dataGiftsDetails?.data?.status?.value;
		return orderStatus === "REDEEMED" || orderStatus === "CLAIMED";
	};

	const handleCompleteClick = () => {
		setShowCompleteModal(true);
	};

	const handleCompleteConfirm = async () => {
		setShowCompleteModal(false);
		setIsCompleting(true);
		setCompleteStatus(null);

		try {
			const orderStatus = dataGiftsDetails?.data?.status?.value;

			// If status is REDEEMED, first claim the order
			if (orderStatus === "REDEEMED") {
				const claimResponse = await authenticatedApi.post(
					`/gifts/${slug}/claim`
				);

				if (claimResponse.data.status !== "success") {
					throw new Error("Failed to claim the order");
				}
			}

			// Then complete the order (for both REDEEMED and CLAIMED statuses)
			const completeResponse = await authenticatedApi.post(
				`/gifts/${slug}/complete`
			);

			if (completeResponse.data.status === "success") {
				setCompleteStatus({
					type: "success",
					message: "Order completed successfully!",
				});
				// Refresh the page data
				setTimeout(() => {
					window.location.reload();
				}, 1500);
			}
		} catch (error) {
			console.error("Complete order error:", error);
			let errorMessage = "An error occurred while completing the order.";

			if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.response?.data?.error) {
				errorMessage = error.response.data.error;
			} else if (error.message) {
				errorMessage = error.message;
			}

			setCompleteStatus({
				type: "error",
				message: errorMessage,
			});
		} finally {
			setIsCompleting(false);
		}
	};

	if (!isEmpty(restaurantId)) {
		restaurantInfo.phoneNumber =
			isEmpty(restaurantId[0].dialCode) || isEmpty(restaurantId[0].mobile)
				? null
				: `${restaurantId[0].dialCode}-${restaurantId[0].mobile}`;
		restaurantInfo.restaurantName = restaurantId[0].restaurantName;
		restaurantInfo.address = restaurantId[0].address;
		restaurantInfo.emailId = restaurantId[0].emailId;
		restaurantInfo.photo = restaurantId[0].photo;
	}

	if (!isEmpty(giftedTo)) {
		giftedToUser.phoneNumber =
			isEmpty(giftedTo[0].dialCode) || isEmpty(giftedTo[0].mobile)
				? null
				: `${giftedTo[0].dialCode}-${giftedTo[0].mobile}`;
		giftedToUser.fullName = giftedTo[0].firstname + giftedTo[0].lastname;
		giftedToUser.emailId = giftedTo[0].emailId;
		giftedToUser.photo = giftedTo[0].photo;
	}

	if (!isEmpty(giftedBy)) {
		giftedByUser.phoneNumber =
			isEmpty(giftedBy[0].dialCode) || isEmpty(giftedBy[0].mobile)
				? null
				: `${giftedBy[0].dialCode}-${giftedBy[0].mobile}`;
		giftedByUser.fullName = giftedBy[0].firstname + " " + giftedBy[0].lastname;
		giftedByUser.emailId = giftedBy[0].emailId;
		giftedByUser.photo = giftedBy[0].photo;
	}

	return (
		<>
			<BreadCrumbs
				title={isFromPickup ? "View Pickup Order" : "View Gift Order"}
				data={[
					{
						label: isFromPickup ? "Pickup Orders" : "Gift Orders",
						path: isFromPickup ? URL.pickupOrders : URL.giftOrders,
					},
					{ label: isFromPickup ? "View Pickup Order" : "View Gift Order" },
				]}
			/>
			<div className="content-wrapper order-details-wrap">
				<div className="d-flex justify-content-between w-100 mb-4">
					<div className="d-flex align-items-center">
						<p className={getBadgeClasses(orderStatus)}>{orderStatus}</p>
					</div>
					<div className="d-flex gap-2">
						{canComplete() && (
							<button
								className="btn btn-success"
								onClick={handleCompleteClick}
								disabled={isCompleting}
							>
								{isCompleting ? "Processing..." : "Complete Order"}
							</button>
						)}
						{canRefund() && (
							<button
								className="btn btn-danger"
								onClick={handleRefundClick}
								disabled={isRefunding}
							>
								{isRefunding ? "Processing..." : "Refund Order"}
							</button>
						)}
					</div>
				</div>

				{completeStatus && (
					<div
						className={`alert ${
							completeStatus.type === "success"
								? "alert-success"
								: "alert-danger"
						} mb-4`}
					>
						{completeStatus.message}
					</div>
				)}

				{refundStatus && (
					<div
						className={`alert ${
							refundStatus.type === "success" ? "alert-success" : "alert-danger"
						} mb-4`}
					>
						{refundStatus.message}
					</div>
				)}
				<div className="card p-0">
					<div className="card-body">
						<div className="form-group row">
							<div className="col-md-6">
								<div className="card-box">
									<div className="card-items">
										<h6>Order Information</h6>
										<p>
											ID : <strong>{dataGiftsDetails?.data?.gift_id}</strong>
										</p>
										<p>
											Date :{" "}
											<strong>
												{getCurrentTimezoneDate(
													dataGiftsDetails?.data?.created_at || "-"
												)}
											</strong>
										</p>
										{/* <p>
											Type : <strong>{orderType}</strong>
										</p> */}
										<p>
											Gift Type :{" "}
											<strong>
												{dataGiftsDetails?.data?.privacy_status || "-"}
											</strong>
										</p>
									</div>
									{paymentDetail && (
										<div className="card-items">
											<h6>Payment Method</h6>
											<p>
												{paymentDetail.card.brand} ....
												{paymentDetail.card.last4}
											</p>
											<p>
												Exp :{" "}
												<strong>
													{paymentDetail.card.exp_month}/
													{paymentDetail.card.exp_year}
												</strong>
											</p>
										</div>
									)}
									<div className="card-items">
										<h6>Restaurant Info</h6>
										<div className="profile-info">
											<div className="personal-info">
												<p>
													<Link
														to={`${URL.restaurants}/${dataGiftsDetails?.data?.restaurant?.id}`}
													>
														<strong>
															{dataGiftsDetails?.data?.restaurant?.name}
														</strong>
													</Link>
												</p>
												<p>
													{dataGiftsDetails?.data?.restaurant?.address_city}
													{","}
													{dataGiftsDetails?.data?.restaurant?.address_country}
													{","}
												</p>
												<p>{dataGiftsDetails?.data?.restaurant?.mobile}</p>
												<p>{dataGiftsDetails?.data?.restaurant?.email}</p>
											</div>
											<div className="user-item">
												<Link
													to={`${URL.restaurants}/${dataGiftsDetails?.data?.restaurant?.id}`}
												>
													{CS.renderImageUrl(
														dataGiftsDetails?.data?.restaurant
															?.profile_image_url
													)}
												</Link>
											</div>
										</div>
									</div>
									<div className="card-items">
										<h6>User Info (Gifted By)</h6>
										<div className="profile-info">
											<div className="personal-info">
												<p>
													<Link
														to={`${URL.onboarderedUsers}/${dataGiftsDetails?.data?.gifter?.id}`}
													>
														<strong>
															{dataGiftsDetails?.data?.gifter?.first_name}
														</strong>{" "}
														<strong>
															{dataGiftsDetails?.data?.gifter?.last_name}
														</strong>
													</Link>
												</p>
												<p>{dataGiftsDetails?.data?.gifter?.mobile}</p>
												<p>{dataGiftsDetails?.data?.gifter?.email}</p>
											</div>
											<div className="user-item">
												<Link
													to={`${URL.onboarderedUsers}/${dataGiftsDetails?.data?.gifter?.id}`}
												>
													{CS.renderImageUrl(
														dataGiftsDetails?.data?.gifter?.profile_image_url
													)}
												</Link>
											</div>
										</div>
									</div>
									<div className="card-items">
										<h6>Gifted To</h6>
										<div className="profile-info">
											<div className="personal-info">
												<p>
													<Link
														to={`${URL.onboarderedUsers}/${dataGiftsDetails?.data?.recipient?.id}`}
													>
														<strong>
															{dataGiftsDetails?.data?.recipient?.first_name}
														</strong>{" "}
														<strong>
															{dataGiftsDetails?.data?.recipient?.last_name}
														</strong>
													</Link>
												</p>
												<p>{dataGiftsDetails?.data?.recipient?.mobile}</p>
												<p>{dataGiftsDetails?.data?.recipient?.email}</p>
											</div>
											<div className="user-item">
												<Link
													to={`${URL.onboarderedUsers}/${dataGiftsDetails?.data?.recipient?.id}`}
												>
													{CS.renderImageUrl(
														dataGiftsDetails?.data?.recipient?.profile_image_url
													)}
												</Link>
											</div>
										</div>
									</div>
									<div className="card-items">
										<h6>Order</h6>
										<ul className="list-group">
											{dataGiftsDetails?.data?.menu_items.map((item, i) => {
												return (
													<li key={i}>
														<p>
															{item.quantity} x {item.name} (${" "}
															{item.price_per_item})
														</p>
														<span>$ {item.total_price}</span>

														{!isEmpty(item.add_ons) && item.add_ons && (
															<>
																<ul className="list-group">
																	{item.add_ons.map((attribute) => (
																		<>
																			<li>
																				<p>
																					{attribute?.quantity} x{" "}
																					{attribute?.name} (${" "}
																					{attribute?.price_per_item})
																				</p>
																				<span>$ {attribute?.total_price}</span>
																			</li>
																		</>
																	))}
																</ul>
															</>
														)}
													</li>
												);
											})}
										</ul>
										<div className="d-flex justify-content-between">
											<p>Item Total: </p>
											<span>
												<strong>
													$ {dataGiftsDetailsOrders?.data?.gross_amount}
												</strong>
											</span>
										</div>
										{restaurantConfigData?.data?.tips_enabled ? (
											<div className="d-flex justify-content-between">
												<p>Tip: </p>
												<span>
													<strong>$ {dataGiftsDetailsOrders?.data?.tip}</strong>
												</span>
											</div>
										) : null}
										{restaurantConfigData?.data?.service_charge_enabled ? (
											<div className="d-flex justify-content-between">
												<p>Service Charge: </p>
												<span>
													<strong>
														$ {dataGiftsDetailsOrders?.data?.service_charge}
													</strong>
												</span>
											</div>
										) : null}
										{rewardPointsUsed && (
											<div className="d-flex justify-content-between danger-text">
												<p className="danger-text">Reward Points Value : </p>
												<span>
													<strong className="danger-text">
														- ${rewardPointsValue}
													</strong>
												</span>
											</div>
										)}
										<div className="d-flex justify-content-between">
											<p>
												Estimated Tax(
												{dataGiftsDetailsOrders?.data?.tax_rate &&
													dataGiftsDetailsOrders?.data?.tax_rate}
												):{" "}
											</p>
											<span>
												<strong>$ {dataGiftsDetailsOrders?.data?.tax}</strong>
											</span>
										</div>
										{dataGiftsDetailsOrders?.data?.discount && (
											<div className="d-flex justify-content-between danger-text">
												<p className="danger-text">Discount : </p>
												<span>
													<strong className="danger-text">
														$ {dataGiftsDetailsOrders?.data?.discount}
													</strong>
												</span>
											</div>
										)}
										{currencyGiftApplied && (
											<div className="d-flex justify-content-between">
												<p className="danger-text">Gift Currency : </p>
												<span>
													<strong className="danger-text">
														- ${currencyGiftId.giftAmount}
													</strong>
												</span>
											</div>
										)}
										{walletAmount && walletAmount > 0 && (
											<React.Fragment>
												<div className="d-flex justify-content-between">
													<p>OpaCash </p>
													<span>
														<strong>$ {walletAmount}</strong>
													</span>
												</div>
											</React.Fragment>
										)}
										<div className="d-flex justify-content-between mt-3">
											<h6 className="">Order Total: </h6>
											<span>
												<strong>
													$ {dataGiftsDetailsOrders?.data?.net_amount}
												</strong>
											</span>
										</div>
									</div>
								</div>
							</div>
							<div className="col-md-6 col-lg-6">
								<div className="card-box">
									{!isEmpty(dataGiftsDetails?.data?.status_history) && (
										<div className="card-items">
											<h6>Status History</h6>
											<ul className="history-list">
												{dataGiftsDetails?.data?.status_history.map((order) => (
													<>
														<li>
															<div className="history-heading">
																<span>{order.value}</span>
																{/* <p>Status by : {"-"}</p> */}
															</div>
															<div className="history-date">
																<span>
																	{getCurrentTimezoneDate(order?.created_at)}
																</span>
															</div>
														</li>
													</>
												))}
											</ul>
										</div>
									)}
									{!isEmpty(dataGiftsDetailsOrders?.data?.coupon_code) && (
										<div className="card-items">
											<h6>Coupon code</h6>
											<p>
												Code:{" "}
												<strong>
													{dataGiftsDetailsOrders?.data?.coupon_code}
												</strong>
											</p>
										</div>
									)}
									{currencyGiftApplied && (
										<div className="card-items">
											<h6>Gift currency code</h6>
											<p>
												Code: <strong>{currencyGiftId.giftCode}</strong>
											</p>
											<p>
												Amount: <strong>$ {currencyGiftId.giftAmount}</strong>
											</p>
										</div>
									)}
									<div className="card-items">
										<h6>Earnings</h6>
										<div className="d-flex justify-content-between">
											<p>Total Amount: </p>
											<span>
												<strong>
													$ {dataGiftsDetailsOrders?.data?.net_amount}
												</strong>
											</span>
										</div>
										<div className="d-flex justify-content-between">
											<p>Commission: </p>
											<span>
												<strong>
													$ {dataGiftsDetailsOrders?.data?.platform_fee}
												</strong>
											</span>
										</div>
										{dataGiftsDetailsOrders?.data?.merchant_fee &&
											dataGiftsDetailsOrders?.data?.merchant_fee > 0 && (
												<React.Fragment>
													<div className="d-flex justify-content-between">
														<p>Total Merchant Fee: </p>
														<span>
															<strong>
																$ {dataGiftsDetailsOrders?.data?.merchant_fee}
															</strong>
														</span>
													</div>
												</React.Fragment>
											)}
										<div className="d-flex justify-content-between">
											<p>Amount To Pay Restaurant: </p>
											<span>
												<strong>
													${" "}
													{dataGiftsDetailsOrders?.data?.net_restaurant_amount}
												</strong>
											</span>
										</div>
										<div className="d-flex justify-content-between">
											<p>Amount To Pay OPA: </p>
											<span>
												<strong>
													$ {dataGiftsDetailsOrders?.data?.net_platform_amount}
												</strong>
											</span>
										</div>
									</div>
									{billSplitStatus && (
										<div className="card-items">
											<h6>Split Bill</h6>
											{sharedBillFriends &&
												sharedBillFriends.map((shareBillFriend) => (
													<>
														<li className="mt-3">
															<div className="d-flex justify-content-between">
																<div className="d-flex align-items-center">
																	<div className="flex-auto">
																		{CS.renderImageUrl(
																			shareBillFriend.friendId.photo
																		)}
																	</div>
																	<div className="d-flex flex-column">
																		<span className="pl-2 user-head">
																			{shareBillFriend.friendId.firstname}{" "}
																			{shareBillFriend.friendId.lastname}
																		</span>
																		<span className="pl-2">
																			{shareBillFriend.friendId.emailId}
																		</span>
																	</div>
																	{shareBillFriend.shareType !== "Self" && (
																		<div className="d-flex flex-column ml-4">
																			<p className="mb-0 badge badge-light-primary">
																				Self
																			</p>
																		</div>
																	)}
																</div>
																<span>
																	<strong>$ {shareBillFriend.amount}</strong>
																</span>
															</div>
														</li>
													</>
												))}
										</div>
									)}
									{!isEmpty(friends) && friends.length !== 0 && (
										<div className="card-items">
											<h6>Friends</h6>
											{friends.map((friend) => (
												<>
													<div className="d-flex align-items-center">
														<div className="flex-auto">
															{CS.renderImageUrl(friend.photo)}
														</div>
														<div className="d-flex flex-column">
															<span className="pl-2 user-head">
																{friend.firstname} {friend.lastname}
															</span>
															<span className="pl-2">{friend.emailId}</span>
														</div>
													</div>
												</>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Refund Modal */}
			<RefundModal
				show={showRefundModal}
				onHide={() => setShowRefundModal(false)}
				onConfirm={handleRefundConfirm}
				orderData={dataGiftsDetails?.data}
				isRefunding={isRefunding}
			/>

			{/* Complete Order Modal */}
			<CompleteOrderModal
				show={showCompleteModal}
				onHide={() => setShowCompleteModal(false)}
				onConfirm={handleCompleteConfirm}
				orderData={dataGiftsDetails?.data}
				isCompleting={isCompleting}
			/>
		</>
	);
};

export default GiftOrderDetails;
